import React from 'react';

interface ProductCardProps {
    product: {
        name: string;
        category: string;
        description: string;
        price: string;
        image: string;
        discountPrice?: string;
        url?: string | null;
    };
}

// Map category names to health category colors
const CATEGORY_COLORS: Record<string, string> = {
    'Hormone Health': '#a0e4dd',
    'Heart Health': '#ef829c',
    'Vitamins & Nutrients': '#ebc191',
    'Nutrient Health': '#ebc191', // Alternative name for vitamins/nutrients
    'Liver & Kidney': '#8fdbb6',
    'Diabetic Risk': '#c7b8de',
};

const ProductCard: React.FC<ProductCardProps> = ({product}) => {
    const {name, category, description, price, image, discountPrice, url} = product;

    // Check if this is the special "You Qualify" category
    const isSpecialCategory = category === 'You Qualify';

    // Get the color for this category
    const categoryColor = CATEGORY_COLORS[category];

    // Handle button click - open URL if available
    const handleButtonClick = () => {
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className="bg-white rounded-lg border border-mld-panel-border flex-shrink-0 w-[263px] sm:w-[243px] touch-manipulation overflow-hidden flex flex-col">
            <div className="h-[160px] overflow-hidden">
                {image ? (
                    <img src={image} alt={name} className="w-full h-full object-cover" />
                ) : (
                    <div className="h-full w-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No Image</span>
                    </div>
                )}
            </div>

            <div className="border-t border-mld-panel-border w-full"></div>

            <div className="p-4 pt-3 flex-1 flex flex-col">
                <div className="mb-3">
                    <span
                        className={`inline-block text-xs px-2 py-1 rounded ${
                            isSpecialCategory ? 'text-white' : 'text-mld-card-text'
                        }`}
                        style={{
                            backgroundColor: isSpecialCategory ? '#8FDDD1' : categoryColor || '#F0F1EB',
                        }}>
                        {category}
                    </span>
                </div>

                <h4
                    className="text-base font-normal text-mld-card-text mb-2 text-left leading-tight"
                    style={{
                        fontFamily: 'Poppins',
                        fontWeight: 400,
                        fontSize: '21px',
                        lineHeight: '25.2px',
                        letterSpacing: '0%',
                    }}>
                    {name}
                </h4>

                <div className="flex items-center text-xs text-mld-card-text mb-2">
                    <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"></polyline>
                    </svg>
                    <span>{description}</span>
                </div>

                <div className="flex-1">
                    <p
                        className="text-2xl font-regular text-mld-card-text mb-1 text-left"
                        style={{
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontSize: '23px',
                            lineHeight: 'auto',
                            letterSpacing: '0%',
                        }}>
                        {price}
                    </p>

                    {discountPrice && (
                        <p className="text-xs text-mld-card-text mb-3 text-left">
                            Only ${discountPrice} with Membership
                        </p>
                    )}
                </div>

                <button
                    onClick={handleButtonClick}
                    className="bg-mld-green-button text-mld-button-text font-medium py-2 px-4 rounded-full text-sm w-full hover:opacity-90 transition-opacity duration-200 mt-auto">
                    {isSpecialCategory ? 'View Benefits' : url ? 'Learn More' : 'Contact Us'}
                </button>
            </div>
        </div>
    );
};

export default ProductCard;
